import { DAEMON_URL } from "../config";

/** Global Emil availability. The daemon proxies this to the qa-agent and
 *  injects the token server-side. `configured` is false when the daemon has no
 *  qa-agent URL/token, in which case the toggle is inert. */
export interface QaAvailability {
  enabled: boolean;
  configured: boolean;
  updatedAt?: string;
  updatedBy?: string | null;
  /** Present when the qa-agent was unreachable / errored (degraded, not fatal). */
  error?: string;
}

/** Read the current global Emil availability. The daemon injects the qa-agent
 *  token and degrades softly (200 + enabled:true) on any upstream/config problem,
 *  so this only throws on a genuine dashboard-auth failure. */
export async function getQaAvailability(): Promise<QaAvailability> {
  const res = await fetch(`${DAEMON_URL}/qa/availability`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch { /* keep status */ }
    throw new Error(msg);
  }
  return (await res.json()) as QaAvailability;
}

/** Enable or disable Emil globally. The daemon injects the qa-agent token
 *  and records the logged-in user's email as the actor. Returns the updated state
 *  so the caller can reconcile its optimistic flip. Throws on a dashboard-auth
 *  failure or an upstream-reported failure so the caller can surface it inline. */
export async function setQaAvailability(enabled: boolean): Promise<QaAvailability> {
  const res = await fetch(`${DAEMON_URL}/qa/availability`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch { /* keep status */ }
    throw new Error(msg);
  }
  return (await res.json()) as QaAvailability;
}
